import { Controller } from '@hotwired/stimulus'
import { Database } from 'firebase-firestore-lite'
import isEqual from 'lodash.isequal'

import type {
  AddPosition,
  InsertPosition,
  Flow,
  HistoryCommand,
  HistoryCommandData,
  HistoryCommandMetadata,
  SetMultipleEmbeddableUpdateCommands,
  UserData,
  HistoryCommandExecutionResult,
} from '../api/models/types'

import { cloneDeep } from 'lodash'
import { getFlow, setFlow } from '../helpers/controllerHelpers/utils'
import { generateId, getWithIds } from '../helpers/utils'
import FlowController from './flow_controller'

declare global {
  interface Array<T> {
    findLastIndex(predicate: (value: T, index: number, obj: T[]) => unknown, thisArg?: any): number
  }
}

//* I store globally the selected flow id
let selectedFlowId: string | null = null

export default class extends Controller {
  db: Database
  declare history: HistoryCommand[]
  declare historyIndex: number
  declare rawSavedFlowJson: Flow
  declare flowId: string
  declare currentVersion: number
  declare isEditingFlowValue: boolean
  declare undoButton: HTMLElement
  declare redoButton: HTMLElement
  declare saveButton: HTMLElement
  declare editHistoryCallbacks: Array<Function>
  declare groupName: string
  declare branchId: string | null

  onClickHandler: (event: Event) => void

  static values = {
    isEditingFlow: false,
  }

  async connect() {
    const isChildFlow = !!this.element.closest('[data-component-type-value="ChildFlow"]')
    if (isChildFlow) return this.removeThisController()

    const self = this
    // this.createButtons()
    this.history = []
    this.historyIndex = 0
    this.editHistoryCallbacks = []
    this.rawSavedFlowJson = getFlow(this, true)
    this.flowId = this.rawSavedFlowJson.id
    this.currentVersion = parseInt(
      document
        .querySelector('[data-controller~="page"]')
        ?.getAttribute('data-page-version-value') || '0'
    )
    this.branchId =
      document
        .querySelector('[data-controller~="page"]')
        ?.getAttribute('data-page-branch-id-value') || null

    this.onClickHandler = (event) => {
      if (this.flowId === selectedFlowId) return

      event.stopPropagation()

      //* I remove the selection from the prev selected flow
      const prevSelectedFlow = document.querySelector(
        `[data-flow-edit-is-editing-flow-value="true"]`
      )
      if (prevSelectedFlow) {
        const prevSelectedFlowEditController =
          self.application.getControllerForElementAndIdentifier(
            prevSelectedFlow,
            'flow-edit'
          ) as any
        if (prevSelectedFlowEditController) {
          prevSelectedFlowEditController.isEditingFlowValue = false
        }
      }

      self.isEditingFlowValue = true
      selectedFlowId = self.flowId
    }
    this.element.addEventListener('click', this.onClickHandler)

    const flowAlreadySelected = document
      .querySelector('savvy-editor')
      ?.getAttribute('data-global-edit-selected-flow-id-value')
    if (!flowAlreadySelected || flowAlreadySelected === this.flowId) {
      self.isEditingFlowValue = true
      selectedFlowId = this.flowId
    }

    if (window.amplitude?.logEvent) {
      const amplitudeLogEvent = window.amplitude.logEvent
      window.amplitude.logEvent = function (...args) {
        const [eventName, eventProperties] = args

        window.Savvy.addEventLogs?.([
          {
            flow_id: this.flowId,
            event_type: 'integration:triggered',
            timestamp: Date.now(),
            user_data_snapshot: this.userDataValue as UserData,
            data: {
              event_name: eventName,
              event_properties: eventProperties,
            },
          },
        ])

        amplitudeLogEvent.apply(this, arguments)
      }
    }
  }

  disconnect() {
    this.element.removeEventListener('click', this.onClickHandler)
  }

  isEditingFlowValueChanged() {
    const editorEl = document.querySelector('savvy-editor')
    const globalEl = document.querySelector('savvy-global')
    const editingFlowIdAttr = 'data-global-edit-selected-flow-id-value'
    const globalFlowIdAttr = 'data-global-selected-flow-id-value'
    if (this.isEditingFlowValue) {
      // this.injectButtons()

      editorEl?.setAttribute(editingFlowIdAttr, this.flowId)
      globalEl?.setAttribute(globalFlowIdAttr, this.flowId)
    } else {
      // this.removeButtons()
      if (editorEl?.getAttribute(editingFlowIdAttr) === this.flowId) {
        editorEl?.setAttribute(editingFlowIdAttr, '')
      }

      if (globalEl?.getAttribute(globalFlowIdAttr) === this.flowId) {
        globalEl?.setAttribute(globalFlowIdAttr, '')
      }
    }
  }

  addToHistory(command: HistoryCommand) {
    //* If the history index is less than the history length, I remove the commands after the current history index
    if (this.historyIndex < this.history.length) this.history.splice(this.historyIndex)

    //* I generate an id for the command
    command.id = generateId('edit')

    //* I add the command to the history
    this.history.push(command)

    //* I update the history index
    this.historyIndex = this.history.length

    //* I update the flow
    this.updateFlow({ type: 'add_edit' })
  }

  watchEditHistory(
    callback: (historyIndex: number, history: HistoryCommand[], flow: Flow) => void
  ): () => void {
    const self = this
    this.editHistoryCallbacks.push(callback)

    return function () {
      const index = self.editHistoryCallbacks.indexOf(callback)
      self.editHistoryCallbacks.splice(index, 1)
    }
  }

  updateFlow({
    type,
    includeLogs = false,
  }: {
    type?: 'undo' | 'redo' | 'add_edit' | 'clear_history' | 'set_history'
    includeLogs?: boolean
  } = {}) {
    const currentJson = this.generateCurrentJson({ includeLogs })
    const flow = includeLogs ? (currentJson as { json: Flow }).json : currentJson

    setFlow(this, flow)
    this.editHistoryCallbacks.forEach((cb) => cb(this.historyIndex, this.history, flow))
    window.dispatchEvent(
      new CustomEvent('savvy:edit_history_updated', {
        detail: { history_index: this.historyIndex, history: this.history, flow, type },
      })
    )
    return currentJson
  }

  generateCurrentJson({
    includeLogs = false,
  }: {
    includeLogs?: boolean
  } = {}): Flow | { json: Flow; logs: HistoryCommandExecutionResult[] } {
    let executionResults: HistoryCommandExecutionResult[] = []
    const self = this

    const index = this.history
      .slice(0, this.historyIndex)
      .findLastIndex((command) => command.type === 'update_entire_flow' && command.json)

    let flowJson: Flow =
      index === -1 ? cloneDeep(this.rawSavedFlowJson) : cloneDeep(this.history[index].json as Flow)

    for (let i = index + 1; i < self.historyIndex; i++) {
      //* Get the command & find the execute function for the command type
      const command = self.history[i]
      const executeFunction: (
        flowJson: Flow,
        command: HistoryCommand
      ) => HistoryCommandExecutionResult | HistoryCommandExecutionResult[] | void =
        EDIT_HISTORY_EXECUTE_FUNCTIONS[command.type]

      if (executeFunction) {
        const results = executeFunction(flowJson, command)

        //* If includelogs is true I need to store the results in the executionResults array
        if (results && !Array.isArray(results)) {
          executionResults.push(results)
        } else if (Array.isArray(results)) {
          executionResults = executionResults.concat(results)
        }
      } else console.warn(`No execute function found for command type: ${command.type}`)
    }

    if (includeLogs) {
      return { json: cloneDeep(flowJson), logs: executionResults }
    } else return cloneDeep(flowJson)
  }

  generateAutoDescription(path: (string | number)[], value: any) {}

  updateEntireFlow(newFlowJson: Flow, metadata: HistoryCommandMetadata, forceAdd?: boolean) {
    const type = 'update_entire_flow'

    if (!forceAdd && isEqual(newFlowJson, this.generateCurrentJson())) return

    this.addToHistory({
      type,
      json: newFlowJson,
      data: undefined,
      metadata,
    })
  }

  updateFlowProperty(
    path: (string | number)[],
    value: any,
    metadata: HistoryCommandMetadata,
    forceAdd?: boolean
  ) {
    const type = 'update_flow_property'

    // Don't add if the value is the same as the current value (unless forceAdd is true)
    // @TODO: Check that this doesn't modify this.rawSavedFlowJson
    const currentValue = getWithIds(this.generateCurrentJson(), path, true)
    if (!forceAdd && isEqual(currentValue, value)) return

    this.addToHistory({
      type,
      data: { path, value },
      metadata,
    })

    if (path[0] === 'computedFields') {
      const flowController: FlowController | null =
        this.application.getControllerForElementAndIdentifier(
          this.element,
          'flow'
        ) as FlowController
      flowController.evaluateComputedFields()
    }
  }

  updateEmbeddableProperty({
    path,
    value,
    metadata,
    objectMatchKey,
    alternativePathObjectMatchKeys,
  }: {
    path: string[]
    value: any
    metadata: HistoryCommandMetadata
    objectMatchKey?: string
    alternativePathObjectMatchKeys?: string[]
  }) {
    const type = 'update_embeddable_property'

    //* Check if the value is the same as the current value, and if so, don't add to history
    const currentValue = getWithIds(this.generateCurrentJson(), path, true)
    if (isEqual(currentValue, value)) return

    this.addToHistory({
      type,
      data: { path, value, objectMatchKey, alternativePathObjectMatchKeys },
      metadata,
    })

    //* If the path is in the computedFields, we need to evaluate the computed fields
    if (path[0] === 'computedFields') {
      const flowController: FlowController | null =
        this.application.getControllerForElementAndIdentifier(
          this.element,
          'flow'
        ) as FlowController
      flowController.evaluateComputedFields()
    }
  }

  addFlowProperty(
    arrayPath: (string | number)[],
    value: any,
    metadata: HistoryCommandMetadata,
    position?: AddPosition
  ) {
    const type = 'add_flow_property'

    this.addToHistory({
      type,
      data: { path: arrayPath, value, position },
      metadata,
    })
  }

  addEmbeddableProperty({
    path,
    value,
    position,
    objectMatchKey,
    alternativePathObjectMatchKeys,
    metadata,
  }: {
    path: string[]
    value: any
    position?: InsertPosition
    objectMatchKey?: string
    alternativePathObjectMatchKeys?: string[]
    metadata: HistoryCommandMetadata
  }) {
    const type = 'add_embeddable_property'

    this.addToHistory({
      type,
      data: { path, value, position, objectMatchKey, alternativePathObjectMatchKeys },
      metadata,
    })
  }

  removeFlowProperty(
    arrayPath: (string | number)[],
    metadata: HistoryCommandMetadata,
    position?: string | number | (string | number)[]
  ) {
    const type = 'remove_flow_property'

    this.addToHistory({
      type,
      data: { path: arrayPath, position },
      metadata,
    })
  }

  removeEmbeddableProperty({
    path,
    propertyToRemove,
    objectMatchKey,
    isStringMatch,
    alternativePathObjectMatchKeys,
    metadata,
  }: {
    path: string[]
    propertyToRemove?: string
    objectMatchKey?: string
    isStringMatch?: boolean
    alternativePathObjectMatchKeys?: string[]
    metadata: HistoryCommandMetadata
  }) {
    const type = 'remove_embeddable_property'

    this.addToHistory({
      type,
      data: {
        path,
        propertyToRemove,
        objectMatchKey,
        isStringMatch,
        alternativePathObjectMatchKeys,
      },
      metadata,
    })
  }

  moveEmbeddableProperty({
    elementId,
    originPath,
    destinationPath,
    position,
    metadata,
  }: {
    elementId: string
    originPath: string[]
    destinationPath?: string[]
    position?: InsertPosition
    metadata: HistoryCommandMetadata
  }) {
    const type = 'move_embeddable_property'

    if (!destinationPath) destinationPath = originPath

    this.addToHistory({
      type,
      data: {
        elementId,
        originPath,
        destinationPath,
        position,
      },
      metadata,
    })
  }

  setMultipleFlowUpdates({
    commands,
    metadata,
  }: {
    commands: SetMultipleEmbeddableUpdateCommands
    metadata: HistoryCommandMetadata
  }) {
    const type = 'set_multiple_embeddable_updates'

    const simpleCommandTypes = {
      add: 'add_embeddable_property',
      remove: 'remove_embeddable_property',
      set: 'update_embeddable_property',
      move: 'move_embeddable_property',
    } as const

    //* Map the commands to the correct type and data
    const mappedCommands = commands.map((command) => ({
      type: simpleCommandTypes[command.type] || command.type,
      data: command.data,
    })) as SetMultipleEmbeddableUpdateCommands

    this.addToHistory({
      type,
      data: { commands: mappedCommands },
      metadata,
    })
  }

  undo(): boolean {
    if (this.historyIndex > 0) {
      this.historyIndex--
      this.updateFlow({ type: 'undo' })
      return true
    }
    return false
  }

  redo(): boolean {
    if (this.historyIndex < this.history.length) {
      this.historyIndex++
      this.updateFlow({ type: 'redo' })
      return true
    }
    return false
  }

  getEditHistory() {
    return { history: this.history, historyIndex: this.historyIndex }
  }

  clearEditHistory(updateFlow = true) {
    this.history = []
    this.historyIndex = 0

    if (updateFlow) this.updateFlow({ type: 'clear_history' })
  }

  setEditHistory(history: HistoryCommand[], historyIndex: number | null) {
    this.history = cloneDeep(history)
    this.historyIndex = historyIndex === null ? this.history.length : historyIndex

    return this.updateFlow({ type: 'set_history', includeLogs: true })
  }

  removeThisController() {
    const controllers = this.element.getAttribute('data-controller')?.split(' ') || []
    const newControllers = controllers.filter((controller) => controller !== 'flow-edit')
    this.element.setAttribute('data-controller', newControllers.join(' '))
  }
}

function setWithIds(
  json: any,
  path: (string | number)[],
  value: any,
  createIfNeeded?: boolean,
  alternativePathObjectMatchKeys?: string[]
) {
  const finalJson = getWithIds(
    json,
    path.slice(0, -1),
    createIfNeeded,
    alternativePathObjectMatchKeys
  )

  finalJson[path[path.length - 1]] = cloneDeep(value)
}

//* Registry of execute functions for HistoryCommand types
const EDIT_HISTORY_EXECUTE_FUNCTIONS: Record<
  HistoryCommand['type'],
  (flowJson: Flow, command: HistoryCommand) => void
> = {
  update_flow_property: (flowJson: Flow, command: HistoryCommand) => {
    const data = command.data as HistoryCommandData['update_flow_property']
    const { path, value } = data
    if (!path) return

    setWithIds(flowJson, path, value, true)
  },
  update_embeddable_property: (
    flowJson: Flow,
    command: HistoryCommand
  ): HistoryCommandExecutionResult => {
    const data = command.data as HistoryCommandData['update_embeddable_property']
    const { path, value, objectMatchKey, alternativePathObjectMatchKeys } = data
    try {
      if (!path) {
        return { command, status: 'ERROR', message: 'No path provided', path: path }
      }

      //* If no objectMatchKey is provided, use the old behavior
      if (!objectMatchKey) {
        setWithIds(flowJson, path, value, true, alternativePathObjectMatchKeys)
        return {
          command,
          status: 'SUCCESS',
          message: `Set property ${path[path.length - 1]}${
            path.length > 1 ? ` in ${path.slice(0, -1).join(' / ')}` : ''
          }`,
          path: path,
        }
      }

      //* If objectMatchKey is provided, find the specific object in the list
      const arrayPath = path.slice(0, -2) //* Path to the array
      const objectIdentifier = path[path.length - 2] //* The value to match (e.g., the URL)
      const propertyToUpdate = path[path.length - 1] //* The key of the property to change (e.g., "tag")

      const list = getWithIds(flowJson, arrayPath, true, alternativePathObjectMatchKeys)
      if (!Array.isArray(list)) {
        //* If path doesn't point to an array, something is wrong.
        //* Fallback to old behavior might be risky, but could be an option.
        setWithIds(flowJson, path, value, true, alternativePathObjectMatchKeys)
        return {
          command,
          status: 'SUCCESS',
          message: `Set property ${path[path.length - 1]}${
            path.length > 1 ? ` in ${path.slice(0, -1).join(' / ')}` : ''
          }`,
          path: path,
        }
      }

      //* Find the specific item using objectMatchKey
      const itemIndex = list.findIndex((item) => item && item[objectMatchKey] === objectIdentifier)
      if (itemIndex === -1) {
        return {
          command,
          status: 'ERROR',
          message: `Could not find the element ${objectIdentifier} in ${arrayPath.join(' / ')}`,
          path: path,
        }
      }

      //* Define new path to the property inside the found object
      const newPath = [...arrayPath, itemIndex, propertyToUpdate]

      //* Update the specific item's value
      setWithIds(flowJson, newPath, value, true, alternativePathObjectMatchKeys)
      return {
        command,
        status: 'SUCCESS',
        message: `Set property ${propertyToUpdate} in ${arrayPath.join(' / ')}`,
        path: newPath as string[],
      }
    } catch (error) {
      console.error(error)
      return {
        command,
        status: 'ERROR',
        message: error.message,
        path: path,
      }
    }
  },
  add_flow_property: (flowJson: Flow, command: HistoryCommand) => {
    const data = command.data as HistoryCommandData['add_flow_property']
    const { path, value, position } = data
    if (!path) return

    let newValue = cloneDeep(value)

    const list = getWithIds(flowJson, path, true)
    if (!Array.isArray(list)) {
      //* If the path does not point to a list, create a list with the newValue
      if (!Array.isArray(newValue)) newValue = [newValue]
      setWithIds(flowJson, path, newValue, true)
      return
    }

    let index: number | undefined
    if (!position) index = list.length
    else {
      const idOrIndex = Object.values(position)[0]
      index =
        typeof idOrIndex === 'number' ? idOrIndex : list.findIndex((item) => item.id === idOrIndex)
      if (typeof position === 'object' && 'after' in position) index++
    }

    if (newValue.isContainerWithChildren) {
      const children = newValue.components.map((c) => ({
        ...c,
        __is_child_already_inserted: true,
      }))
      list.splice(index, 0, newValue, ...children)
    } else {
      if (Array.isArray(newValue)) {
        list.splice(index, 0, ...newValue)
      } else {
        list.splice(index, 0, newValue)
      }
    }
  },
  add_embeddable_property: (
    flowJson: Flow,
    command: HistoryCommand
  ): HistoryCommandExecutionResult => {
    const warnings = {
      after: false,
      before: false,
      inside: false,
    }
    const data = command.data as HistoryCommandData['add_embeddable_property']
    const { path, value, position, objectMatchKey = 'id', alternativePathObjectMatchKeys } = data
    if (!path) {
      return {
        command,
        status: 'ERROR',
        message: 'No path provided',
        path: path,
      }
    }

    let newValue = cloneDeep(value)

    newValue = Array.isArray(newValue) ? newValue : [newValue] //* For adding arrays we only handle a container at the beggining with childrens

    try {
      let list = getWithIds(flowJson, path, true, alternativePathObjectMatchKeys)
      if (!Array.isArray(list)) {
        //* If the path does not point to a list, create an empty array
        setWithIds(flowJson, path, [], true, alternativePathObjectMatchKeys)
        list = getWithIds(flowJson, path, true, alternativePathObjectMatchKeys)
      }

      //* Check if we're moving components in different scopes
      function isGlobalComponentsPath(path: string[]) {
        return path[0] === 'components' && !path[1]
      }

      function isPageComponentsPath(path: string[]) {
        return path[0] === 'pages' && path[2] === 'components' && !path[3]
      }

      const isAddingComponent = isGlobalComponentsPath(path) || isPageComponentsPath(path)

      //* Helper function to insert all elements
      const insertAllElements = (insertIndex: number) => {
        list.splice(insertIndex, 0, ...newValue)
      }

      //* Try to position based on priority: after > before > inside > globalLocation
      if (position) {
        //* Priority 1: after
        if (position.after) {
          const afterIndex = list.findIndex(
            (item) => item && item[objectMatchKey] === position.after
          )
          if (afterIndex !== -1) {
            if (isAddingComponent) {
              const afterElement = list[afterIndex]
              afterElement.parent_id
                ? (newValue[0].parent_id = afterElement.parent_id)
                : delete newValue[0].parent_id
              afterElement._location
                ? (newValue[0]._location = afterElement._location)
                : delete newValue[0]._location
              //* I also clean parent_key and _location if not needed
              if (newValue[0].parent_key) delete newValue[0].parent_key
            }

            insertAllElements(afterIndex + 1)
            return {
              command,
              status: 'SUCCESS',
              message: `Added ${newValue.length} element${newValue.length > 1 ? 's' : ''}`,
              path: path,
            }
          }
          warnings.after = true
        }

        //* Priority 2: before
        if (position.before) {
          const beforeIndex = list.findIndex(
            (item) => item && item[objectMatchKey] === position.before
          )
          //* If I found the before element, insert the elements to move before it
          if (beforeIndex !== -1) {
            //* If adding a component I need to add the parent_id or _location of the before component
            if (isAddingComponent) {
              const beforeElement = list[beforeIndex]
              beforeElement.parent_id
                ? (newValue[0].parent_id = beforeElement.parent_id)
                : delete newValue[0].parent_id
              beforeElement._location
                ? (newValue[0]._location = beforeElement._location)
                : delete newValue[0]._location
              //* I also clean parent_key and _location if not needed
              if (newValue[0].parent_key) delete newValue[0].parent_key
            }
            insertAllElements(beforeIndex)
            if (warnings.after) {
              return {
                command,
                status: 'WARNING',
                message: `Could not find after element, inserted element using before reference`,
                path: path,
              }
            }
            return {
              command,
              status: 'SUCCESS',
              message: `Added ${newValue.length} element${newValue.length > 1 ? 's' : ''}`,
              path: path,
            }
          }
          warnings.before = true
        }

        //* Priority 3: inside
        if (
          isAddingComponent &&
          position.inside &&
          Array.isArray(position.inside) &&
          position.inside.length > 0
        ) {
          for (const parentId of [...position.inside].reverse()) {
            const parentContainerIndex = list.findIndex(
              (item) => item && item[objectMatchKey] === parentId
            )
            if (parentContainerIndex !== -1) {
              const parentContainer = list[parentContainerIndex]
              //* If parentContainer type is not Container, continue
              if (parentContainer.type !== 'Container') continue

              //* Update the parent_id property of the new component
              newValue[0].parent_id = parentId
              //* I also clean parent_key and _location if not needed
              if (newValue[0].parent_key) delete newValue[0].parent_key

              insertAllElements(list.length)
              if (warnings.after || warnings.before) {
                return {
                  command,
                  status: 'WARNING',
                  message: `Could not find ${warnings.after ? 'after element' : ''}${
                    warnings.before ? `${warnings.after ? ' and' : ''} before element` : ''
                  }, inserted inside parent reference`,
                  path: path,
                }
              }
              return {
                command,
                status: 'SUCCESS',
                message: `Added ${newValue.length} element${newValue.length > 1 ? 's' : ''}`,
                path: path,
              }
            }
          }
          warnings.inside = true
        }

        //* Priority 4: globalLocation
        if (isAddingComponent && position.globalLocation && isGlobalComponentsPath(path)) {
          //* Set the _location property to the specified globalLocation
          newValue[0]._location = position.globalLocation
          //* Remove parent_id since component is now in a specific global location
          if (newValue[0].parent_id) delete newValue[0].parent_id
          if (newValue[0].parent_key) delete newValue[0].parent_key
          insertAllElements(list.length)
          return {
            command,
            status: 'SUCCESS',
            message: `Added ${newValue.length} element${newValue.length > 1 ? 's' : ''}`,
            path: path,
          }
        }
      }

      //* Default: insert at the end if no position worked. and delete parent_id
      if (newValue[0].parent_id) delete newValue[0].parent_id
      if (newValue[0].parent_key) delete newValue[0].parent_key
      if (newValue[0]._location) delete newValue[0]._location
      insertAllElements(list.length)
      if (warnings.after || warnings.before || warnings.inside) {
        return {
          command,
          status: 'WARNING',
          message: `Could not find ${warnings.after ? 'after element' : ''}${
            warnings.before ? `${warnings.after ? ' and' : ''} before element` : ''
          }${
            warnings.inside
              ? `${
                  warnings.after && warnings.before
                    ? ' and'
                    : warnings.after || warnings.before
                    ? ','
                    : ''
                } inside parent`
              : ''
          }, inserted at the end of the list`,
          path: path,
        }
      }
      return {
        command,
        status: 'SUCCESS',
        message: `Added ${newValue.length} element${newValue.length > 1 ? 's' : ''}`,
        path: path,
      }
    } catch (error) {
      console.error(error)
      return {
        command,
        status: 'ERROR',
        message: error.message,
        path: path,
      }
    }
  },
  remove_flow_property: (flowJson: Flow, command: HistoryCommand) => {
    const data = command.data as HistoryCommandData['remove_flow_property']
    const { path, position } = data
    if (!path) return

    const list = getWithIds(flowJson, path)

    if (!Array.isArray(list)) {
      throw new Error('Path does not point to a list')
    }

    let indexToDelete = list.length - 1 //* By default, delete the last element

    if (Array.isArray(position)) {
      //* If the position is an array of indexes
      const indexes = position.map((i) =>
        typeof i === 'number' ? i : list.findIndex((item) => item.id === i)
      )
      const sortedIndexes = indexes.sort((a, b) => b - a) //* Sort from highest to lowest to avoid deletion problems
      sortedIndexes.forEach((i) => {
        if (i >= 0 && i < list.length) {
          list.splice(i, 1) //* Delete the element at the specified index
        }
      })
    } else {
      //* If the position is a single ID or index
      const idOrIndex = position
      if (idOrIndex === 0 || idOrIndex === '0') {
        indexToDelete = 0
      } else {
        indexToDelete =
          typeof idOrIndex === 'number'
            ? idOrIndex
            : list.findIndex((item) => item.id === idOrIndex)
      }

      if (indexToDelete >= 0 && indexToDelete < list.length) {
        //* Ensure index is within array bounds
        list.splice(indexToDelete, 1) //* Delete the element at the specified position
      }
    }
  },
  remove_embeddable_property: (
    flowJson: Flow,
    command: HistoryCommand
  ): HistoryCommandExecutionResult => {
    const data = command.data as HistoryCommandData['remove_embeddable_property']
    const {
      path,
      propertyToRemove,
      objectMatchKey = 'id',
      isStringMatch,
      alternativePathObjectMatchKeys,
    } = data

    try {
      //* If there is no path or the path is not an array, return
      if (!path || !Array.isArray(path)) {
        return {
          command,
          status: 'ERROR',
          message: 'No path provided',
          path: path,
        }
      }

      //* If no propertyToRemove is provided, We are removing the item matching the path
      if (!propertyToRemove) {
        //* Get the list to remove the item from
        const list = getWithIds(flowJson, path.slice(0, -1), true, alternativePathObjectMatchKeys)

        //* If the list is not an array, return
        if (!Array.isArray(list)) {
          return {
            command,
            status: 'ERROR',
            message: `${path.slice(0, -1).join(' / ')} is not a list`,
            path: path,
          }
        }

        //* Find the index item to remove
        const itemToRemoveIndex = list.findIndex((item) => {
          if (isStringMatch) return item === path[path.length - 1]
          else return item[objectMatchKey] === path[path.length - 1]
        })

        //* If the item is not found, return
        if (itemToRemoveIndex === -1)
          return {
            command,
            status: 'ERROR',
            message: `Could not find the element ${path[path.length - 1]} in ${path
              .slice(0, -1)
              .join(' / ')}`,
            path: path,
          }

        //* Remove the item from the list
        list.splice(itemToRemoveIndex, 1)
      }

      //* If a propertyToRemove is provided, we are removing the property from the item matching the path
      if (propertyToRemove) {
        //* Get the item to remove the property from
        const item = getWithIds(flowJson, path, true, alternativePathObjectMatchKeys)

        //* If the item is not an object, return
        if (typeof item !== 'object' || !item) {
          return {
            command,
            status: 'ERROR',
            message: `Could not find the element ${path[path.length - 1]} in ${path
              .slice(0, -1)
              .join(' / ')}`,
            path: path,
          }
        }

        //* Remove the property from the item
        delete item[propertyToRemove]
      }
      return {
        command,
        status: 'SUCCESS',
        message: `Removed property ${propertyToRemove} from ${path.slice(0, -1).join(' / ')}`,
        path: path,
      }
    } catch (error) {
      console.error(error)
      return {
        command,
        status: 'ERROR',
        message: error.message,
        path: path,
      }
    }
  },
  move_embeddable_property: (
    flowJson: Flow,
    command: HistoryCommand
  ): HistoryCommandExecutionResult => {
    const warnings = {
      after: false,
      before: false,
      inside: false,
    }
    const data = command.data as HistoryCommandData['move_embeddable_property']
    const { elementId, originPath, destinationPath, position } = data

    try {
      if (!elementId || !originPath || !destinationPath) {
        throw new Error('Missing required parameters: elementId, originPath, destinationPath')
      }

      //* Get the origin array (where the component currently is)
      const originArray = getWithIds(flowJson, originPath)
      if (!Array.isArray(originArray)) {
        throw new Error(`Origin path ${originPath.join(' / ')} does not point to an array`)
      }

      //* Get the destination array
      let destinationArray
      try {
        destinationArray = getWithIds(flowJson, destinationPath) //* Don't create, just check
      } catch (error) {
        if (error.message === 'no-last-step') {
          //* If the destination path does not exist, create an empty array
          setWithIds(flowJson, destinationPath, [])
          destinationArray = getWithIds(flowJson, destinationPath)
        } else {
          throw error
        }
      }

      if (!Array.isArray(destinationArray)) {
        throw new Error(
          `Destination path ${destinationPath.join(' / ')} does not point to an array`
        )
      }

      //* Find the component index in origin array
      const elementIndex = originArray.findIndex((item) => item && item.id === elementId)
      if (elementIndex === -1) {
        throw new Error(`Element with ID ${elementId} not found in ${originPath.join(' / ')}`)
      }

      //* Get the component to move (clone it to avoid reference issues)
      const elementToMove = cloneDeep(originArray[elementIndex])

      //* Check if we're moving components in different scopes
      function isGlobalComponentsPath(path: string[]) {
        return path[0] === 'components' && !path[1]
      }
      function isPageComponentsPath(path: string[]) {
        return path[0] === 'pages' && path[2] === 'components' && !path[3]
      }

      const isMovingComponent =
        isGlobalComponentsPath(originPath) || isPageComponentsPath(originPath)
      const isMovingComponentFromGlobalToPage =
        isGlobalComponentsPath(originPath) && isPageComponentsPath(destinationPath)

      //* If moving component from global to page, remove _location property
      if (isMovingComponentFromGlobalToPage) delete elementToMove._location

      //* Check if it's a Container and collect all components to move
      const isContainer = elementToMove.type === 'Container'
      const elementsToMove = [elementToMove]

      if (isContainer) {
        //* Find all components that have this container as parent_id
        for (const component of originArray) {
          if (component && component.parent_id === elementId) {
            elementsToMove.push(cloneDeep(component))
          }
        }
      }

      //* Remove all elements to move from origin array (in reverse order)
      const elementsToRemove = elementsToMove.map((comp) => comp.id)
      for (let i = originArray.length - 1; i >= 0; i--) {
        if (originArray[i] && elementsToRemove.includes(originArray[i].id)) {
          originArray.splice(i, 1)
        }
      }

      //* Helper function to insert all elements
      const insertAllElements = (insertIndex: number) => {
        elementsToMove.forEach((comp, index) => {
          destinationArray.splice(insertIndex + index, 0, comp)
        })
      }

      //* Try to position based on priority: after > before > inside > globalLocation
      if (position) {
        //* Priority 1: after
        if (position.after) {
          const afterIndex = destinationArray.findIndex(
            (item) => item && item.id === position.after
          )
          //* If I found the after element, insert the elements to move after it
          if (afterIndex !== -1) {
            //* If moving a component I need to add the parent_id or _location of the after component
            if (isMovingComponent) {
              const afterElement = destinationArray[afterIndex]
              afterElement.parent_id
                ? (elementsToMove[0].parent_id = afterElement.parent_id)
                : delete elementsToMove[0].parent_id
              afterElement._location
                ? (elementsToMove[0]._location = afterElement._location)
                : delete elementsToMove[0]._location
              //* I also clean parent_key and _location if not needed
              if (elementsToMove[0].parent_key) delete elementsToMove[0].parent_key
              if (isMovingComponentFromGlobalToPage) delete elementsToMove[0]._location
            }
            insertAllElements(afterIndex + 1)
            return {
              command,
              status: 'SUCCESS',
              message: `Moved ${elementsToMove.length} element${
                elementsToMove.length > 1 ? 's' : ''
              }`,
              path: originPath,
            }
          }
          warnings.after = true
        }

        //* Priority 2: before
        if (position.before) {
          const beforeIndex = destinationArray.findIndex(
            (item) => item && item.id === position.before
          )
          //* If I found the before element, insert the elements to move before it
          if (beforeIndex !== -1) {
            //* If moving a component I need to add the parent_id or _location of the before component
            if (isMovingComponent) {
              const beforeElement = destinationArray[beforeIndex]
              beforeElement.parent_id
                ? (elementsToMove[0].parent_id = beforeElement.parent_id)
                : delete elementsToMove[0].parent_id
              beforeElement._location
                ? (elementsToMove[0]._location = beforeElement._location)
                : delete elementsToMove[0]._location

              //* I also clean parent_key and _location if not needed
              if (elementsToMove[0].parent_key) delete elementsToMove[0].parent_key
              if (isMovingComponentFromGlobalToPage) delete elementsToMove[0]._location
            }
            insertAllElements(beforeIndex)
            if (warnings.after) {
              return {
                command,
                status: 'WARNING',
                message: `Could not move element after element, inserting before element`,
                path: originPath,
              }
            }
            return {
              command,
              status: 'SUCCESS',
              message: `Moved ${elementsToMove.length} element${
                elementsToMove.length > 1 ? 's' : ''
              }`,
              path: originPath,
            }
          }
          warnings.before = true
        }

        //* Priority 3: inside
        if (
          isMovingComponent &&
          position.inside &&
          Array.isArray(position.inside) &&
          position.inside.length > 0
        ) {
          for (const parentId of [...position.inside].reverse()) {
            const parentContainerIndex = destinationArray.findIndex(
              (item) => item && item.id === parentId
            )
            if (parentContainerIndex !== -1) {
              const parentContainer = destinationArray[parentContainerIndex]
              //* If parentConteinar type is not Container, continue
              if (parentContainer.type !== 'Container') continue

              //* Update the parent_id property of the main component
              elementsToMove[0].parent_id = parentId
              //* I also clean parent_key and _location if not needed
              if (elementsToMove[0].parent_key) delete elementsToMove[0].parent_key
              if (isMovingComponentFromGlobalToPage) delete elementsToMove[0]._location

              insertAllElements(destinationArray.length)
              if (warnings.after || warnings.before) {
                return {
                  command,
                  status: 'WARNING',
                  message: `Could not move element ${warnings.after ? 'after element' : ''}${
                    warnings.before ? `${warnings.after ? ' and' : ''} before element` : ''
                  }, moving inside parent`,
                  path: originPath,
                }
              }
              return {
                command,
                status: 'SUCCESS',
                message: `Moved ${elementsToMove.length} element${
                  elementsToMove.length > 1 ? 's' : ''
                }`,
                path: originPath,
              }
            }
          }
          warnings.inside = true
        }

        //* Priority 4: globalLocation
        if (
          isMovingComponent &&
          position.globalLocation &&
          isGlobalComponentsPath(destinationPath)
        ) {
          //* Set the _location property to the specified globalLocation
          elementsToMove[0]._location = position.globalLocation
          //* Remove parent_id since component is now in a specific global location
          if (elementsToMove[0].parent_id) delete elementsToMove[0].parent_id
          if (elementsToMove[0].parent_key) delete elementsToMove[0].parent_key
          insertAllElements(destinationArray.length)
          return {
            command,
            status: 'SUCCESS',
            message: `Moved ${elementsToMove.length} element${
              elementsToMove.length > 1 ? 's' : ''
            }`,
            path: originPath,
          }
        }
      }

      //* Default: insert at the end if no position worked. and delete parent_id
      //* Remove parent_id only from the main component, not from container children
      if (elementsToMove[0].parent_id) delete elementsToMove[0].parent_id
      if (elementsToMove[0].parent_key) delete elementsToMove[0].parent_key
      if (elementsToMove[0]._location) delete elementsToMove[0]._location

      insertAllElements(destinationArray.length)

      if (warnings.after || warnings.before || warnings.inside) {
        return {
          command,
          status: 'WARNING',
          message: `Could not move element ${warnings.after ? 'after element' : ''}${
            warnings.before ? `${warnings.after ? ',' : ''} before element` : ''
          }${
            warnings.inside
              ? `${
                  warnings.after && warnings.before
                    ? ' and'
                    : warnings.after || warnings.before
                    ? ','
                    : ''
                } inside parent container`
              : ''
          }, inserted at the end of the list`,
          path: originPath,
        }
      }
      return {
        command,
        status: 'SUCCESS',
        message: `Moved ${elementsToMove.length} element${elementsToMove.length > 1 ? 's' : ''}`,
        path: originPath,
      }
    } catch (error) {
      console.error(error)
      return {
        command,
        status: 'ERROR',
        message: error.message,
        path: originPath,
      }
    }
  },
  set_multiple_embeddable_updates: (
    flowJson: Flow,
    command: HistoryCommand
  ): HistoryCommandExecutionResult[] => {
    const executionResults: HistoryCommandExecutionResult[] = []
    const data = command.data as HistoryCommandData['set_multiple_embeddable_updates']
    const { commands } = data
    if (!commands || !Array.isArray(commands)) return executionResults

    commands.forEach((subCommand) => {
      const executeFunction = EDIT_HISTORY_EXECUTE_FUNCTIONS[subCommand.type]
      if (executeFunction) {
        //* Create a temporary command object to execute the sub-command
        const tempCommand = {
          type: subCommand.type,
          data: subCommand.data,
          metadata: command.metadata,
        } as HistoryCommand
        const result = executeFunction(
          flowJson,
          tempCommand
        ) as HistoryCommandExecutionResult | null | void
        if (result && Object.keys(result).length > 0) {
          executionResults.push(result)
        }
      } else {
        throw new Error(`No execute function found for sub-command type: ${subCommand.type}`)
      }
    })
    return executionResults
  },
  update_entire_flow: () => {},
}
